/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { Customer, CustomersDataActions } from '@msdyn365-commerce/retail-proxy';

import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Input class for get customer data action.
 */
export class GetCustomerInput implements IActionInput {
    public customerAccountNumber?: string;

    private readonly apiSettings: ICommerceApiSettings;

    private readonly cacheType?: CacheType;

    public constructor(apiSettings: ICommerceApiSettings, accountNumber?: string, cacheType?: CacheType) {
        this.customerAccountNumber = accountNumber;
        this.apiSettings = apiSettings;
        this.cacheType = cacheType;
    }

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => buildCacheKey(`Customer-${this.customerAccountNumber}`, this.apiSettings);

    /**
     * GetCacheObjectType.
     * @returns - Cache Object Type string.
     */
    public getCacheObjectType = (): string => 'Customer';

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => {
        return this.cacheType ? this.cacheType : 'request';
    };
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData -- The input data passed to the createInput method.
 * @returns - ProductAvailabilitiesForSelectedVariant or error.
 */
const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetCustomerInput => {
    const { user, apiSettings } = inputData.requestContext;
    if (!user.token || !user.isAuthenticated) {
        throw new Error('[getCustomer][createInput]: User is not Authenticated.');
    }

    return new GetCustomerInput(apiSettings);
};

/**
 * GetCustomerAction.
 * @param input - GetCustomerInput.
 * @param ctx - Action Context.
 * @returns - Customer information.
 */
const getCustomerAction = async (input: GetCustomerInput, ctx: IActionContext): Promise<Customer> => {
    if (input && ctx.requestContext.user.isAuthenticated) {
        // TO-DO- Need to clean up this code once SDK releases new proxy that handles null.
        return CustomersDataActions.readAsync({ callerContext: ctx, bypassCache: 'get' }, input.customerAccountNumber || '').then(
            response => {
                return Array.isArray(response) ? response[0] : response;
            }
        );
    }

    ctx.trace('[getCustomer]Invalid input passed to action');
    return <Customer>{};
};

/**
 * The customer data action.
 */
export const getCustomerActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-customer',
    action: <IAction<Customer>>getCustomerAction,
    input: createInput
});

export default getCustomerActionDataAction;
